import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import addAddressSchemas from "@rentiohq/shared-frontend/dist/forms/addAddress";
import kycSchemas from "@rentiohq/shared-frontend/dist/forms/kyc";
import * as kycActions from "@rentiohq/shared-frontend/dist/forms/kyc/schema.kyc.actions";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as formActions from "@rentiohq/shared-frontend/dist/redux/form/form.actions";
import { createForm } from "@rentiohq/shared-frontend/dist/redux/form/form.actions";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { UiSchema } from "react-native-jsonschema-form-test";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Navigate } from "react-router-dom";
import { IRootState } from "redux/reducers";
import FormMultistep from "../../../../components/FormMultistep";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import { getReferrer } from "../../../../utils/navigation.utils";
import * as S from "./KnowYourCustomer.styles";

const KnowYourCustomerBase = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const user = useSelector((state: IRootState) => authSelectors.getUser(state));

  const [preventRedirect, setPreventRedirect] = React.useState(true);
  const [formIdKyc] = React.useState(generateFormId());
  const [formIdAddAddress, setFormIdAddAddress] = React.useState<
    number | undefined
  >(undefined);

  // Lifecycle
  React.useEffect(() => {
    dispatch(
      createForm({
        formId: formIdKyc,
        schemas: kycSchemas({
          user,
        }),
        withSummary: false,
      }),
    );
  }, []);

  // Event handlers
  const handleCustomSuccessActionKyc = () => {
    setPreventRedirect(false);
  };

  const handleCustomSubmitActionAddAddress = (formData: any) => {
    if (!formIdAddAddress) {
      return;
    }

    dispatch(
      formActions.updatePartialFormData({
        formId: formIdKyc,
        stepIndex: 0,
        fieldName: "address",
        fieldData: formData,
      }),
    );

    setFormIdAddAddress(undefined);
  };

  const handleSelectMore = (
    formId: number | string,
    stepIndex: number,
    fieldName: string,
    fieldValue: any,
    uiSchema: UiSchema,
    formData: any,
    extraData?: { [key: string]: any },
  ) => {
    switch (uiSchema["ui:field"]) {
      case ERentioFormField.Address: {
        const formIdAddAddress = generateFormId();

        dispatch(
          createForm({
            formId: formIdAddAddress,
            schemas: addAddressSchemas(fieldValue, {
              withGoogleMapsInput: false,
            }),
            withSummary: false,
          }),
        );

        setFormIdAddAddress(formIdAddAddress);

        break;
      }

      default:
        break;
    }
  };

  // Render
  const renderKnowYourCustomerForm = () => {
    if (!user) {
      return;
    }

    return (
      <FormMultistep
        formId={formIdKyc}
        // @ts-ignore state match
        isPerformingSubmitActionSelector={kycActions.isPerformingSubmitActionSelector(
          user,
        )}
        // @ts-ignore state match
        performingSubmitActionErrorSelector={kycActions.performingSubmitActionErrorSelector(
          user,
        )}
        submitActionCreator={kycActions.submitActionCreator(user)}
        customSuccessAction={handleCustomSuccessActionKyc}
        submitTitle={getLocalizedText("payment.online.kyc.cta.submit")}
        onSelectMore={handleSelectMore}
      />
    );
  };

  const renderAddAddressForm = () => {
    if (!user) {
      return;
    }

    if (!formIdAddAddress) {
      return;
    }

    return (
      <FormMultistep
        formId={formIdAddAddress}
        customSubmitAction={handleCustomSubmitActionAddAddress}
        submitTitle={getLocalizedText("payment.online.add_address.cta.submit")}
        onSelectMore={handleSelectMore}
      />
    );
  };

  const renderRedirect = () => {
    if (preventRedirect) {
      return null;
    }

    return <Navigate to={getReferrer(location)} />;
  };

  if (!user) {
    return;
  }

  return (
    <>
      {renderRedirect()}
      <Page>
        <InfoHeader
          title={getLocalizedText(
            formIdAddAddress
              ? "payment.online.kyc.address.title"
              : "payment.online.kyc.title",
          )}
          body={getLocalizedText(
            formIdAddAddress
              ? "payment.online.kyc.address.body"
              : "payment.online.kyc.body",
          )}
        />
        <S.FormWrap>
          {!!formIdAddAddress
            ? renderAddAddressForm()
            : renderKnowYourCustomerForm()}
        </S.FormWrap>
      </Page>
    </>
  );
};

export const KnowYourCustomer = KnowYourCustomerBase;
